import { SubstepType } from "~/features/configurator/Linear/dtos";
import { KmatType } from "~/features/configurator/Linear/kmatType";
import {
  type CarriageKmatSummaryData,
  makeLabelGetter,
  makeNumberGetter,
  prepareSubStepsInfo,
  type RailKmatSummaryData,
  type SummaryData,
  type SummaryDataProvider,
} from "~/features/configurator/Shaft/SummaryDataProvider";
import * as UnitCstics from "~/features/configurator/Linear/v2/configuration/classic/UnitCstics";
import * as RailCstics from "~/features/configurator/Rail/v2/configuration/classic/RailsCstics";
import * as CarriageCstics from "~/features/configurator/Carriage/v2/configuration/CarriageCstics";
import { isNonNull } from "~/common/utils/reducers";
import { getMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";
import { removeMultipleTags } from "~/common/utils/domUtils";
import type { LinearCstic } from "~/features/configurator/Linear/models";
import { NumberFormatter } from "~/common/utils/NumberFormatter";
import { FAKE_CSTIC_UNIT_LENGTH } from "~/features/configurator/Linear/constants";

export function getStep1SummaryData(summaryProvider: SummaryDataProvider): SummaryData[] | null {
  const substeps: SubstepType[] = [];
  return prepareSubStepsInfo(summaryProvider, substeps);
}

export function getStep2SummaryData(summaryProvider: SummaryDataProvider): SummaryData[] | null {
  const substeps: SubstepType[] = [SubstepType.DesignType, SubstepType.Size];
  return prepareSubStepsInfo(summaryProvider, substeps);
}

export function getStep3SummaryData(summaryProvider: SummaryDataProvider): SummaryData[] | null {
  const summaryData = prepareSubStepsInfo(summaryProvider, [SubstepType.SystemCoating]) ?? [];
  const t = summaryProvider.t;

  const lengthSubstep = summaryProvider.substeps?.Length;
  const cstics = getMainKmat({ kmats: summaryProvider.kmats }).cstics;
  const length = cstics[FAKE_CSTIC_UNIT_LENGTH].value!;
  if (lengthSubstep) {
    summaryData.push({
      name: t(lengthSubstep.name),
      value: NumberFormatter.getDefaultFormatter().format(+length),
      isStandard: false,
    });
  }

  summaryData.push(...(prepareSubStepsInfo(summaryProvider, [SubstepType.Preload, SubstepType.Accuracy]) ?? []));

  return summaryData;
}

export function getStep4SummaryData(summaryProvider: SummaryDataProvider): SummaryData[] | null {
  const substeps: SubstepType[] = [];
  const summaryData = prepareSubStepsInfo(summaryProvider, substeps) ?? [];

  // Locating face...
  const t = summaryProvider.t;
  const railAmount = summaryProvider.kmats[KmatType.SubRail]?.length ?? 1;

  [
    UnitCstics.RailLocatingFaceSegment1,
    UnitCstics.CarriageLocatingFaceSegment1,
    railAmount == 2 && UnitCstics.RailLocatingFaceSegment2,
    railAmount == 2 && UnitCstics.CarriageLocatingFaceSegment2,
  ]
    .filter(isNonNull)
    .map((name) => {
      const cstics = getMainKmat({ kmats: summaryProvider.kmats }).cstics;
      return cstics[name];
    })
    .filter((cstic) => cstic?.value)
    .forEach((cstic) => {
      summaryData.push({
        name: t(cstic.label),
        isStandard: false,
        value: t(cstic.values.find((v) => v.value === cstic.value)?.label ?? ""),
      });
    });

  summaryData.forEach((data) => {
    data.value = removeMultipleTags(data.value, "p", "span")!;
    data.name = removeMultipleTags(data.name, "p", "span")!;
  });
  return summaryData;
}

export function getStep5SummaryData(summaryProvider: SummaryDataProvider): SummaryData[] | null {
  const substeps: SubstepType[] = [SubstepType.Lubrication, SubstepType.Attachment, SubstepType.Closure];
  return prepareSubStepsInfo(summaryProvider, substeps);
}

export function getStep6SummaryDataRail(summaryProvider: SummaryDataProvider): RailKmatSummaryData[] | null {
  const guidewaySetDetailsStep = summaryProvider.substeps[SubstepType.GuidewaySet];
  if (!guidewaySetDetailsStep) {
    return null;
  }

  const t = summaryProvider.t;

  const summaryData: RailKmatSummaryData[] = [];

  const inputCsticNames = [
    RailCstics.GuidewayLength,
    RailCstics.AL,
    RailCstics.AR,
    RailCstics.PartAmount,
    RailCstics.PartSizing,
    RailCstics.PartOneLength,
    RailCstics.PartTwoLength,
    RailCstics.PartThreeLength,
    RailCstics.PartFourLength,
    RailCstics.PartFiveLength,
  ];

  const inputCsticNamesFormatting: Record<string, (cstic: LinearCstic) => string> = {
    [RailCstics.GuidewayLength]: makeNumberGetter(),
    [RailCstics.AL]: makeNumberGetter(),
    [RailCstics.AR]: makeNumberGetter(),
    [RailCstics.PartSizing]: makeLabelGetter(t),
    [RailCstics.PartAmount]: makeLabelGetter(t),
    [RailCstics.PartOneLength]: makeNumberGetter(),
    [RailCstics.PartTwoLength]: makeNumberGetter(),
    [RailCstics.PartThreeLength]: makeNumberGetter(),
    [RailCstics.PartFourLength]: makeNumberGetter(),
    [RailCstics.PartFiveLength]: makeNumberGetter(),
  };

  inputCsticNames.forEach((inputCsticName) => {
    // const subkmats = summaryProvider.kmats[guidewaySetDetailsStep.kmatType];
    // const mainDef = getSubKmatCstics(guidewaySetDetailsStep, 0).input[inputCsticName];
    const subkmats = summaryProvider.kmats[KmatType.SubRail];
    const getValue = inputCsticNamesFormatting[inputCsticName];
    const firstSubkmat = subkmats[0];
    // const nameKey = firstSubkmat.cstics[mainDef.name].label;

    const cstic = subkmats.map((kmat) => kmat.cstics[inputCsticName]).find(isNonNull);
    if (!cstic) {
      return;
    }
    const nameKey = cstic.label;

    const entry: RailKmatSummaryData = {
      name: t(nameKey),
      rail: [],
    };
    subkmats.forEach((subkmat, index) => {
      //   const def = getSubKmatCstics(guidewaySetDetailsStep, index).input[inputCsticName];
      //   const cstic = subkmat.cstics[def.name];
      const cstic = subkmat.cstics[inputCsticName];
      entry.rail.push({
        isStandard: cstic ? getIsStandard(cstic) : false,
        value: cstic ? getValue(cstic) : "",
      });
    });
    summaryData.push(entry);
  });

  summaryData.forEach((data) => {
    data.name = removeMultipleTags(data.name, "p", "span")!;
    data.rail.forEach((entry) => {
      entry.value = removeMultipleTags(entry.value, "p", "span")!;
    });
  });

  return summaryData;
}

export function getStep7SummaryData(summaryProvider: SummaryDataProvider): SummaryData[] | null {
  const substeps: SubstepType[] = [SubstepType.ReferenceCarriage, SubstepType.SMDS];

  // Carriage Details
  return prepareSubStepsInfo(summaryProvider, substeps);
}

export function getStep7SummaryDataCarriage(summaryProvider: SummaryDataProvider): CarriageKmatSummaryData[] | null {
  const carriageDetailsStep = summaryProvider.substeps[SubstepType.CarriageDetails];
  if (!carriageDetailsStep) {
    return null;
  }

  const railAmount = summaryProvider.kmats[KmatType.SubRail].length;
  const carriageAmount = summaryProvider.kmats[KmatType.SubCarriage].length;
  const carriagesPerRail = carriageAmount / railAmount;

  const t = summaryProvider.t;

  const summaryData: CarriageKmatSummaryData[] = [];

  const inputCsticNames = [
    CarriageCstics.CarriageDesign,
    CarriageCstics.KitsLeft,
    CarriageCstics.KitsRight,
    CarriageCstics.KitsCenter,
    CarriageCstics.LubricationConnectorOrientation,
    CarriageCstics.LubricationConnectorPosition,
    CarriageCstics.LubricationConnector,
    CarriageCstics.LubricationConnectorAlignment,
  ] as const;

  const inputCsticNamesFormatting: Record<(typeof inputCsticNames)[number], (cstic: LinearCstic) => string> = {
    [CarriageCstics.CarriageDesign]: makeLabelGetter(t),
    [CarriageCstics.KitsLeft]: makeLabelGetter(t),
    [CarriageCstics.KitsRight]: makeLabelGetter(t),
    [CarriageCstics.KitsCenter]: makeLabelGetter(t),
    [CarriageCstics.LubricationConnectorOrientation]: makeLabelGetter(t),
    [CarriageCstics.LubricationConnectorPosition]: makeLabelGetter(t),
    [CarriageCstics.LubricationConnector]: makeLabelGetter(t),
    [CarriageCstics.LubricationConnectorAlignment]: makeLabelGetter(t),
  };

  inputCsticNames.forEach((inputCsticName) => {
    // const subkmats = summaryProvider.kmats[carriageDetailsStep.kmatType];
    // const mainDef = getSubKmatCstics(carriageDetailsStep, 0).input[inputCsticName];
    const subkmats = summaryProvider.kmats[KmatType.SubCarriage];
    const getValue = inputCsticNamesFormatting[inputCsticName];
    const firstSubkmat = subkmats[0];
    // const nameKey = firstSubkmat.cstics[mainDef.name].label;

    const cstic = subkmats.map((kmat) => kmat.cstics[inputCsticName]).find(isNonNull);
    if (!cstic) {
      return;
    }
    const nameKey = cstic.label;

    const entry: CarriageKmatSummaryData = {
      name: t(nameKey),
      rail: [],
    };
    for (let railIndex = 0; railIndex < railAmount; railIndex++) {
      const rail: CarriageKmatSummaryData["rail"][0] = {
        carriage: [],
      };
      for (let carriageIndex = 0; carriageIndex < carriagesPerRail; carriageIndex++) {
        const flatCarriageIndex = railIndex * carriagesPerRail + carriageIndex;
        const subkmat = subkmats[flatCarriageIndex];
        // const def = getSubKmatCstics(carriageDetailsStep, flatCarriageIndex).input[inputCsticName];
        // const cstic = subkmat.cstics[def.name];
        const cstic = subkmat.cstics[inputCsticName];
        rail.carriage.push({
          isStandard: getIsStandard(cstic),
          value: getValue(cstic),
        });
      }
      entry.rail.push(rail);
    }
    summaryData.push(entry);
  });

  summaryData.forEach((data) => {
    data.name = removeMultipleTags(data.name, "p", "span")!;
    data.rail.forEach((rail) => {
      rail.carriage.forEach((entry) => {
        entry.value = removeMultipleTags(entry.value, "p", "span")!;
      });
    });
  });

  return summaryData;
}

function getIsStandard(cstic: LinearCstic) {
  const cValue = cstic.values.find((cVal) => cVal.value === cstic.value);
  return cValue?.standard ?? false;
}
